import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { format } from 'date-fns';
import {
  AlertOctagonIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  EyeIcon,
  InfoIcon,
  Loader2Icon,
  PlusIcon
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { PdfViewerDialog } from '@/components/reports/PdfViewerDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@/components/ui/pagination';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useMainSidebar } from '@/hooks/useMainSidebar';
import { useReportPolling } from '@/hooks/useReportPolling';
import {
  CREATE_EWR_REPORT,
  CREATE_LOAN_TAPE_REPORT,
  CREATE_PID_REPORT,
  type EWRReportHistoryNode,
  GET_EWR_REPORT,
  GET_EWR_REPORT_HISTORY,
  GET_ILEVEL_ASSETS,
  GET_LOAN_TAPE_FUNDS,
  GET_LOAN_TAPE_REPORT,
  GET_LOAN_TAPE_REPORT_HISTORY,
  GET_PID_REPORT,
  GET_PID_REPORT_HISTORY,
  GET_QUEUE_POSITION,
  GET_REPORT_STATUS,
  type GetEwrReportHistoryResponse,
  type GetEwrReportResponse,
  type GetILevelAssetsResponse,
  type GetLoanTapeFundsResponse,
  type GetLoanTapeReportHistoryResponse,
  type GetLoanTapeReportResponse,
  type GetPidReportHistoryResponse,
  type GetPidReportResponse,
  type GetReportStatusResponse,
  type ILevelAssetNode,
  type LoanTapeReportHistoryNode,
  type PIDReportHistoryNode,
  type QueuePositionData
} from '@/lib/graphql/reports';
import { encodeReportId } from '@/utils/reportPolling';

function ActionButtons({
  isDownloading,
  onDownload,
  onInfo,
  onView,
  optimisticReports,
  report
}: {
  isDownloading?: boolean;
  onDownload: () => void;
  onInfo: () => void;
  onView: () => void;
  optimisticReports?: Map<string, any>;
  report: any;
}) {
  const status = getReportStatus(report, optimisticReports);
  const isPidReport = report.reportTypeTag === 'PID';

  // For PID reports, use reportId.id; for EWR reports, use id
  const baseReportId =
    isPidReport && 'reportId' in report && report.reportId
      ? report.reportId.id
      : report.id;
  const reportId = report.backendReportId || baseReportId;

  const hasValidReportId = reportId && String(reportId).trim() !== '';
  const isDisabled =
    status === 'cancelled' ||
    status === 'in-progress' ||
    !hasValidReportId ||
    isDownloading;

  const disabledReason = !hasValidReportId
    ? 'Report ID not available - report may still be processing'
    : status === 'cancelled'
      ? 'Report was cancelled'
      : status === 'in-progress'
        ? 'Report is still being generated'
        : isDownloading
          ? 'Downloading report...'
          : '';

  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        className="h-8 w-8 p-0"
        disabled={isDisabled}
        onClick={onView}
        size="icon"
        title={disabledReason || 'View report'}
        variant="outline"
      >
        <EyeIcon className="h-3.5 w-3.5" />
      </Button>
      <Button
        className="h-8 w-8 p-0"
        disabled={isDisabled}
        onClick={onDownload}
        size="icon"
        title={disabledReason || 'Download report'}
        variant="default"
      >
        {isDownloading ? (
          <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <DownloadIcon className="h-3.5 w-3.5" />
        )}
      </Button>
      <Button
        className="h-8 w-8 p-0"
        onClick={onInfo}
        size="icon"
        title="View report details"
        variant="secondary"
      >
        <InfoIcon className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function Avatar({ initials }: { initials: string }) {
  return (
    <div className="bg-muted flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-medium">
      {initials}
    </div>
  );
}

function downloadBase64File(params: { dataBase64: string; filename: string }) {
  try {
    // Clean and normalize the base64 string
    let base64Data = params.dataBase64.trim();

    // Remove Python bytes literal prefix if present (b'...' or b"...")
    if (base64Data.startsWith("b'") || base64Data.startsWith('b"')) {
      base64Data = base64Data.slice(2); // Remove b' or b"
      // Remove trailing quote if present
      if (base64Data.endsWith("'") || base64Data.endsWith('"')) {
        base64Data = base64Data.slice(0, -1);
      }
    }

    // Remove data URL prefix if present (e.g., "data:application/...;base64,")
    const commaIndex = base64Data.indexOf(',');
    if (commaIndex !== -1) {
      base64Data = base64Data.slice(commaIndex + 1);
    }

    // Remove any whitespace, newlines, or other non-base64 characters
    base64Data = base64Data.replace(/\s/g, '');

    // Handle URL-encoded base64 (decode %2B to +, %2F to /, etc.)
    try {
      base64Data = decodeURIComponent(base64Data);
    } catch {
      // If URL decoding fails, use the original string
      // This is fine as the base64 might not be URL-encoded
    }

    // Validate base64 format (should only contain A-Z, a-z, 0-9, +, /, and = for padding)
    // Allow for potential truncation in preview, so we'll be more lenient
    const base64Pattern = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Pattern.test(base64Data)) {
      // Try to extract just the base64 portion if there's extra content
      const base64Match = /[A-Za-z0-9+/]+={0,2}/.exec(base64Data);
      if (base64Match) {
        base64Data = base64Match[0];
      } else {
        throw new Error(
          `Invalid base64 string format. Preview: ${base64Data.substring(0, 50)}...`
        );
      }
    }

    // Convert base64 string to binary
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create a Blob from the binary data
    const mimeType = getMimeTypeFromFilename(params.filename);
    const blob = new Blob([bytes], { type: mimeType });

    // Create an object URL from the Blob
    const url = URL.createObjectURL(blob);

    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = params.filename;
    document.body.appendChild(link);
    link.click();

    // Clean up: remove link and revoke object URL
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    console.error('Base64 data preview:', params.dataBase64.substring(0, 100));
    throw error;
  }
}

// Helper function to format date for display
function formatDate(dateString: null | string | undefined): null | string {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return format(date, 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

// Helper function to format datetime for display
function formatDateTime(dateString: null | string | undefined): null | string {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return format(date, 'MMM d, yyyy, h:mm a');
  } catch {
    return dateString;
  }
}

// Helper function to get initials from name
function getInitials(name: string): string {
  if (!name) return '??';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getMimeTypeFromFilename(filename?: null | string): string {
  const lower = (filename ?? '').toLowerCase();
  if (lower.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
  return 'application/octet-stream';
}

function getReportStatus(
  report:
    | EWRReportHistoryNode
    | LoanTapeReportHistoryNode
    | PIDReportHistoryNode,
  optimisticReports?: Map<string, any>
): 'cancelled' | 'completed' | 'in-progress' {
  const generationStatus = (report as any)?.generationStatus as
    | null
    | string
    | undefined;

  if (optimisticReports?.has(report.id)) {
    return 'in-progress';
  }

  if (generationStatus) {
    const normalizedStatus = generationStatus.toLowerCase().trim();

    if (normalizedStatus === 'completed') return 'completed';
    if (normalizedStatus === 'failed' || normalizedStatus === 'failure')
      return 'cancelled';
    if (normalizedStatus === 'in_progress') return 'in-progress';
    if (normalizedStatus === 'pending') return 'completed';
  }

  return 'completed';
}

function getReportType(
  report: any
): 'EWR' | 'Loan Tape' | 'Loan Tape 2.0' | 'PID' | 'PID L1' {
  const reportTag = report.reportTypeTag;
  if (reportTag === 'PID') {
    return 'PID';
  }
  if (reportTag === 'LOAN_TAPE') {
    return 'Loan Tape';
  }
  return 'EWR';
}

function NewReportDialog({
  existingReports,
  onCreate,
  onOpenChange,
  open
}: {
  existingReports: any[];
  onCreate: (data: {
    asOfDate: Date;
    // PID-specific fields
    assetId?: string;
    assetName?: string;
    borrowerStatusDate?: Date;
    financialsPeriodEnd?: Date;
    // Loan Tape-specific fields
    fundId?: string;
    leveragePeriodEnd?: Date;
    periodEnd?: Date;
    reportDate: Date;
    reportType: 'EWR' | 'Loan Tape' | 'PID';
    securityStatusDate?: Date;
  }) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [reportType, setReportType] = useState<'EWR' | 'Loan Tape' | 'PID'>(
    'EWR'
  );
  const [reportDate, setReportDate] = useState<Date | undefined>(new Date());
  const [asOfDate, setAsOfDate] = useState<Date | undefined>(new Date());
  const [leveragePeriodEnd, setLeveragePeriodEnd] = useState<Date | undefined>(
    new Date()
  );

  // PID-specific fields
  const [assetId, setAssetId] = useState<string>('');
  const [periodEnd, setPeriodEnd] = useState<Date | undefined>(new Date());
  const [financialsPeriodEnd, setFinancialsPeriodEnd] = useState<
    Date | undefined
  >(new Date());

  // Loan Tape-specific fields
  const [fundId, setFundId] = useState('');
  const [securityStatusDate, setSecurityStatusDate] = useState<
    Date | undefined
  >(new Date());
  const [borrowerStatusDate, setBorrowerStatusDate] = useState<
    Date | undefined
  >(new Date());

  // Fetch loan tape funds for dropdown
  const { data: loanTapeFundsData, loading: loanTapeFundsLoading } =
    useQuery<GetLoanTapeFundsResponse>(GET_LOAN_TAPE_FUNDS, {
      errorPolicy: 'all',
      skip: reportType !== 'Loan Tape'
    });

  // Fetch iLevel assets for PID report dropdown
  const { data: assetsData, loading: assetsLoading } =
    useQuery<GetILevelAssetsResponse>(GET_ILEVEL_ASSETS, {
      fetchPolicy: 'cache-first',
      skip: reportType !== 'PID' || currentStep !== 2
    });

  const ilevelAssets: ILevelAssetNode[] =
    assetsData?.ilevelAssets?.edges?.map(edge => edge.node) || [];
  const sortedIlevelAssets = useMemo(
    () =>
      [...ilevelAssets].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [ilevelAssets]
  );

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setCurrentStep(1);
      setReportType('EWR');
      setReportDate(new Date());
      setAsOfDate(new Date());
      setLeveragePeriodEnd(new Date());
      setAssetId('');
      setPeriodEnd(new Date());
      setFinancialsPeriodEnd(new Date());
      setFundId('');
      setSecurityStatusDate(new Date());
      setBorrowerStatusDate(new Date());
    }
  }, [open]);

  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSubmit = () => {
    // Validation based on report type
    if (reportType === 'PID') {
      if (!assetId || !financialsPeriodEnd || !periodEnd || !asOfDate) {
        toast.error('Please fill in all required fields for PID report');
        return;
      }
    } else if (reportType === 'Loan Tape') {
      if (
        !fundId ||
        !periodEnd ||
        !asOfDate ||
        !securityStatusDate ||
        !borrowerStatusDate
      ) {
        toast.error('Please fill in all required fields for Loan Tape report');
        return;
      }
    } else {
      if (!reportDate || !asOfDate) {
        toast.error('Please select both Period End and As of Date');
        return;
      }
    }

    const selectedAsset =
      reportType === 'PID' && assetId
        ? ilevelAssets.find(asset => asset.id === assetId)
        : null;

    onCreate({
      asOfDate: asOfDate || new Date(),
      // PID-specific fields
      assetId: reportType === 'PID' ? assetId : undefined,
      assetName: reportType === 'PID' ? selectedAsset?.name : undefined,
      // Loan Tape-specific fields
      borrowerStatusDate:
        reportType === 'Loan Tape' ? borrowerStatusDate : undefined,
      financialsPeriodEnd:
        reportType === 'PID' ? financialsPeriodEnd : undefined,
      fundId: reportType === 'Loan Tape' ? fundId : undefined,
      leveragePeriodEnd: leveragePeriodEnd || undefined,
      periodEnd:
        reportType === 'PID' || reportType === 'Loan Tape'
          ? periodEnd
          : undefined,
      reportDate:
        reportType === 'Loan Tape'
          ? periodEnd || reportDate || new Date()
          : reportDate || new Date(),
      reportType,
      securityStatusDate:
        reportType === 'Loan Tape' ? securityStatusDate : undefined
    });

    // Reset form
    setCurrentStep(1);
    setReportType('EWR');
    setReportDate(new Date());
    setAsOfDate(new Date());
    setLeveragePeriodEnd(new Date());
    setAssetId('');
    setPeriodEnd(new Date());
    setFinancialsPeriodEnd(new Date());
    setFundId('');
    setSecurityStatusDate(new Date());
    setBorrowerStatusDate(new Date());
    onOpenChange(false);
  };

  const totalSteps = 2;

  // Check if a duplicate report was created in the last 15 minutes
  const hasDuplicateRecentReport = () => {
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);

    if (reportType === 'EWR') {
      // EWR reports need reportDate, asOfDate, and leveragePeriodEnd
      if (!reportDate || !asOfDate) return false;

      const reportDateStr = format(reportDate, 'yyyy-MM-dd');
      const asOfDateStr = format(asOfDate, 'yyyy-MM-dd');
      const leveragePeriodEndStr = leveragePeriodEnd
        ? format(leveragePeriodEnd, 'yyyy-MM-dd')
        : null;

      return existingReports.some(report => {
        // Only check EWR reports
        if ('reportTypeTag' in report && report.reportTypeTag !== 'EWR')
          return false;

        const reportCreatedDate = report.createDate
          ? new Date(report.createDate)
          : null;
        const matchesReportDate = report.reportDate === reportDateStr;
        const matchesAsOfDate = report.asOfDate === asOfDateStr;
        const matchesLeveragePeriod = leveragePeriodEndStr
          ? report.leveragePeriodEnd === leveragePeriodEndStr
          : !report.leveragePeriodEnd || report.leveragePeriodEnd === null;
        const isRecent =
          reportCreatedDate && reportCreatedDate >= fifteenMinutesAgo;

        return (
          matchesReportDate &&
          matchesAsOfDate &&
          matchesLeveragePeriod &&
          isRecent
        );
      });
    } else if (reportType === 'PID') {
      // PID reports need assetId, financialsPeriodEnd, periodEnd, and asOfDate
      if (!assetId || !financialsPeriodEnd || !periodEnd || !asOfDate)
        return false;

      const asOfDateStr = format(asOfDate, 'yyyy-MM-dd');
      const periodEndStr = format(periodEnd, 'yyyy-MM-dd');

      return existingReports.some(report => {
        // Only check PID reports
        if ('reportTypeTag' in report && report.reportTypeTag !== 'PID')
          return false;

        // For PID reports, we need to check the reportId nested object
        const pidReport =
          'reportId' in report &&
          report.reportId &&
          typeof report.reportId === 'object'
            ? report.reportId
            : null;
        if (!pidReport) return false;

        const reportCreatedDate = report.createDate
          ? new Date(report.createDate)
          : null;
        // Compare by finding the asset with matching ID from the selected assetId
        const selectedAsset = ilevelAssets.find(asset => asset.id === assetId);
        const matchesAsset =
          selectedAsset && pidReport.investmentName === selectedAsset.name;
        // Note: We no longer check reportDate for PID reports
        const matchesPeriodEnd = pidReport.periodEnd === periodEndStr;
        const matchesAsOfDate = pidReport.asOfDate === asOfDateStr;
        const isRecent =
          reportCreatedDate && reportCreatedDate >= fifteenMinutesAgo;

        return matchesAsset && matchesPeriodEnd && matchesAsOfDate && isRecent;
      });
    } else if (reportType === 'Loan Tape') {
      if (
        !fundId ||
        !periodEnd ||
        !asOfDate ||
        !securityStatusDate ||
        !borrowerStatusDate
      ) {
        return false;
      }

      const periodEndStr = format(periodEnd, 'yyyy-MM-dd');
      const asOfDateStr = format(asOfDate, 'yyyy-MM-dd');
      const securityStatusDateStr = format(securityStatusDate, 'yyyy-MM-dd');
      const borrowerStatusDateStr = format(borrowerStatusDate, 'yyyy-MM-dd');

      return existingReports.some(report => {
        if ('reportTypeTag' in report && report.reportTypeTag !== 'LOAN_TAPE') {
          return false;
        }

        const reportCreatedDate = report.createDate
          ? new Date(report.createDate)
          : null;
        const isRecent =
          reportCreatedDate && reportCreatedDate >= fifteenMinutesAgo;

        const matchesFund = report.fundId === fundId;
        const matchesPeriodEnd = report.periodEnd === periodEndStr;
        const matchesAsOfDate = report.asOfDate === asOfDateStr;
        const matchesSecurityStatus =
          report.securityStatusDate === securityStatusDateStr;
        const matchesBorrowerStatus =
          report.borrowerStatusDate === borrowerStatusDateStr;

        return (
          matchesFund &&
          matchesPeriodEnd &&
          matchesAsOfDate &&
          matchesSecurityStatus &&
          matchesBorrowerStatus &&
          isRecent
        );
      });
    }

    return false;
  };

  const showDuplicateWarning = hasDuplicateRecentReport();

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogBody>
          {/* Header */}
          <DialogHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-foreground text-lg font-semibold">
                Create A New Report
              </h2>
              <Badge variant="yellow">Unsaved</Badge>
            </div>
          </DialogHeader>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2.5 px-5">
            <span
              className={`text-sm font-normal ${
                currentStep === 1 ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              Step 1
            </span>
            <ChevronRightIcon className="text-muted-foreground h-[15px] w-[15px]" />
            <span
              className={`text-sm font-normal ${
                currentStep === 2 ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              Step 2
            </span>
          </div>

          {/* Step Content */}
          <div className="flex flex-col gap-5 p-5">
            {currentStep === 1 && (
              <>
                {/* Radio Group */}
                <RadioGroup
                  className="flex flex-col gap-3"
                  onValueChange={value => {
                    setReportType(value as 'EWR' | 'Loan Tape' | 'PID');
                  }}
                  value={reportType}
                >
                  <div className="flex items-start gap-3">
                    <Field orientation="horizontal">
                      <RadioGroupItem
                        className="mt-0.5"
                        id="ewr"
                        value="EWR"
                      />
                      <FieldLabel
                        className="flex-1 cursor-pointer text-sm font-medium"
                        htmlFor="ewr"
                      >
                        EWR
                      </FieldLabel>
                    </Field>
                  </div>
                  <div className="flex items-start gap-3">
                    <Field orientation="horizontal">
                      <RadioGroupItem
                        className="mt-0.5"
                        id="pid"
                        value="PID"
                      />
                      <FieldLabel
                        className="flex-1 cursor-pointer text-sm font-medium"
                        htmlFor="pid"
                      >
                        PID
                      </FieldLabel>
                    </Field>
                  </div>
                  <div className="flex items-start gap-3">
                    <Field orientation="horizontal">
                      <RadioGroupItem
                        className="mt-0.5"
                        id="loan-tape"
                        value="Loan Tape"
                      />
                      <FieldLabel
                        className="flex-1 cursor-pointer text-sm font-medium"
                        htmlFor="loan-tape"
                      >
                        Loan Tape
                      </FieldLabel>
                    </Field>
                  </div>
                </RadioGroup>
              </>
            )}

            {currentStep === 2 && (
              <>
                <div className="flex flex-col gap-4">
                  {/* EWR Report Fields */}
                  {reportType === 'EWR' && (
                    <>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="report-date">
                            Period End *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="report-date"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {reportDate ? (
                                  format(reportDate, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setReportDate}
                                selected={reportDate}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="as-of-date">
                            As of Date *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="as-of-date"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {asOfDate ? (
                                  format(asOfDate, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setAsOfDate}
                                selected={asOfDate}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="leverage-period-end">
                            Leverage Period End (Optional)
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="leverage-period-end"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {leveragePeriodEnd ? (
                                  format(leveragePeriodEnd, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setLeveragePeriodEnd}
                                selected={leveragePeriodEnd}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                    </>
                  )}

                  {/* PID Report Fields */}
                  {reportType === 'PID' && (
                    <>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="asset-id">
                            Investment Name *
                          </FieldLabel>
                          <Select
                            onValueChange={setAssetId}
                            value={assetId}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue
                                placeholder={
                                  assetsLoading
                                    ? 'Loading assets...'
                                    : 'Select an investment'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {assetsLoading ? (
                                <SelectItem
                                  disabled
                                  value="loading"
                                >
                                  Loading...
                                </SelectItem>
                              ) : ilevelAssets.length === 0 ? (
                                <SelectItem
                                  disabled
                                  value="no-assets"
                                >
                                  No assets available
                                </SelectItem>
                              ) : (
                                sortedIlevelAssets.map(asset => (
                                  <SelectItem
                                    key={asset.id}
                                    value={asset.id}
                                  >
                                    {asset.name}{' '}
                                    {asset.excelTicker
                                      ? `(${asset.excelTicker})`
                                      : ''}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="period-end">
                            Period End *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="period-end"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {periodEnd ? (
                                  format(periodEnd, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setPeriodEnd}
                                selected={periodEnd}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="pid-as-of-date">
                            As of Date *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="pid-as-of-date"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {asOfDate ? (
                                  format(asOfDate, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setAsOfDate}
                                selected={asOfDate}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="financials-period-end">
                            Financials Period End *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="financials-period-end"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {financialsPeriodEnd ? (
                                  format(financialsPeriodEnd, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setFinancialsPeriodEnd}
                                selected={financialsPeriodEnd}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                    </>
                  )}

                  {/* Loan Tape Report Fields */}
                  {reportType === 'Loan Tape' && (
                    <>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="loan-tape-fund">
                            Fund *
                          </FieldLabel>
                          <Select
                            onValueChange={setFundId}
                            value={fundId}
                          >
                            <SelectTrigger id="loan-tape-fund">
                              <SelectValue placeholder="Select a fund" />
                            </SelectTrigger>
                            <SelectContent>
                              {loanTapeFundsLoading ? (
                                <div className="flex items-center justify-center py-2">
                                  <Loader2Icon className="h-4 w-4 animate-spin" />
                                </div>
                              ) : loanTapeFundsData?.loanTapeFunds?.length ? (
                                <>
                                  <SelectItem value="0">All Funds</SelectItem>
                                  {loanTapeFundsData.loanTapeFunds
                                    .filter(fund => fund && fund.id)
                                    .map(fund => (
                                      <SelectItem
                                        key={fund.id}
                                        value={fund.id}
                                        disabled={fund.disabled === true}
                                      >
                                        {fund.name}
                                      </SelectItem>
                                    ))}
                                </>
                              ) : (
                                <div className="text-muted-foreground py-2 text-center text-sm">
                                  No funds available
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="loan-tape-period-end">
                            Period End *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="loan-tape-period-end"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {periodEnd ? (
                                  format(periodEnd, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setPeriodEnd}
                                selected={periodEnd}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="loan-tape-as-of-date">
                            As of Date *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="loan-tape-as-of-date"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {asOfDate ? (
                                  format(asOfDate, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setAsOfDate}
                                selected={asOfDate}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="loan-tape-security-status-date">
                            Security Status Date *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="loan-tape-security-status-date"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {securityStatusDate ? (
                                  format(securityStatusDate, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setSecurityStatusDate}
                                selected={securityStatusDate}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field>
                          <FieldLabel htmlFor="loan-tape-borrower-status-date">
                            Borrower Status Date *
                          </FieldLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="w-full justify-start text-left font-normal"
                                id="loan-tape-borrower-status-date"
                                variant="outline"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {borrowerStatusDate ? (
                                  format(borrowerStatusDate, 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">
                                    Pick a date
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                            >
                              <Calendar
                                mode="single"
                                onSelect={setBorrowerStatusDate}
                                selected={borrowerStatusDate}
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                      </div>
                    </>
                  )}
                </div>

                {/* Duplicate Report Warning - Only show on Step 2 if duplicate found */}
                {showDuplicateWarning && (
                  <Alert className="border-amber-300 bg-amber-50">
                    <AlertOctagonIcon className="h-4 w-4 text-amber-500" />
                    <AlertDescription>
                      <p className="text-sm font-medium text-amber-500">
                        {reportType === 'PID'
                          ? 'A report with these dates and investment name was recently created in the last 15 minutes. Please verify if you need to create another one.'
                          : 'A report with these dates was recently created in the last 15 minutes. Please verify if you need to create another one.'}
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </div>

          {/* Footer */}
        </DialogBody>
        <DialogFooter>
          {currentStep === 1 ? (
            <Button
              key="1"
              onClick={handleNext}
            >
              Create New Report
            </Button>
          ) : (
            <Fragment key="2">
              <Button
                onClick={handleBack}
                variant="outline"
              >
                <ChevronLeftIcon />
                Back
              </Button>
              <Button
                disabled={
                  reportType === 'PID'
                    ? !assetId ||
                      !financialsPeriodEnd ||
                      !periodEnd ||
                      !asOfDate
                    : reportType === 'Loan Tape'
                      ? !fundId ||
                        !periodEnd ||
                        !asOfDate ||
                        !securityStatusDate ||
                        !borrowerStatusDate
                      : !reportDate || !asOfDate
                }
                onClick={handleSubmit}
              >
                Create Report
              </Button>
            </Fragment>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QueuePositionDialog({
  error,
  loading,
  onOpenChange,
  open,
  queueData,
  reportType
}: {
  error?: Error | null;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  queueData: null | QueuePositionData;
  reportType?: 'EWR' | 'Loan Tape' | 'PID' | null;
}) {
  const formatEstimatedTime = (minutes: null | number): string => {
    if (minutes === null || minutes === undefined) return 'N/A';
    if (minutes < 1) return 'Less than 1 minute';
    if (minutes < 60)
      return `${Math.round(minutes)} minute${Math.round(minutes) !== 1 ? 's' : ''}`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (mins === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
    return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
  };

  // Backend returns the correct estimated time, just display it
  const getTotalEstimatedTime = (): null | number => {
    if (!queueData) return null;
    return queueData.estimatedTimeMinutes;
  };

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Report Queue Status</DialogTitle>
          <DialogDescription>
            Your report has been queued for generation.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-4 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2Icon className="text-muted-foreground h-6 w-6 animate-spin" />
                <span className="text-muted-foreground ml-2 text-sm">
                  Checking queue position...
                </span>
              </div>
            ) : queueData ? (
              <>
                <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20">
                  <InfoIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <AlertDescription>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      {queueData.message}
                    </p>
                  </AlertDescription>
                </Alert>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">
                      Queue Position
                    </Label>
                    <p className="mt-1 text-lg font-semibold">
                      {queueData.position === 0
                        ? 'Next in line'
                        : `#${queueData.position + 1}`}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      Total Jobs Ahead
                    </Label>
                    <p className="mt-1 text-lg font-semibold">
                      {queueData.totalJobs}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">
                      Estimated Time
                    </Label>
                    <p className="mt-1 text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {formatEstimatedTime(getTotalEstimatedTime())}
                    </p>
                  </div>
                </div>
              </>
            ) : error ? (
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <AlertOctagonIcon className="text-destructive h-6 w-6" />
                <p className="text-destructive text-sm font-medium">
                  Unable to fetch queue information
                </p>
                <p className="text-muted-foreground text-center text-xs">
                  {error.message ||
                    'An error occurred while fetching queue position'}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <InfoIcon className="text-muted-foreground h-6 w-6" />
                <p className="text-muted-foreground text-sm">
                  Queue information is not available
                </p>
                <p className="text-muted-foreground text-center text-xs">
                  Your report has been queued, but queue position details could
                  not be retrieved.
                </p>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            className="bg-[#003455] hover:bg-[#003455]/90"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportInfoDialog({
  onOpenChange,
  open,
  optimisticReports,
  report
}: {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  optimisticReports?: Map<string, any>;
  report:
    | EWRReportHistoryNode
    | LoanTapeReportHistoryNode
    | null
    | PIDReportHistoryNode;
}) {
  if (!report) return null;

  const reportTag = (report as any).reportTypeTag;
  const isPidReport = reportTag === 'PID';
  const isLoanTape = reportTag === 'LOAN_TAPE';
  const status = getReportStatus(report, optimisticReports);

  // Get PID-specific fields
  const investmentName =
    isPidReport && 'reportId' in report && report.reportId
      ? (report.reportId as any).investmentName || '-'
      : null;

  const periodEnd =
    isPidReport && 'reportId' in report && report.reportId
      ? (report.reportId as any).periodEnd || null
      : isLoanTape
        ? ((report as any).periodEnd as null | string) || null
        : null;

  const loanTapeFund: null | string = isLoanTape
    ? ((report as any).fundName as null | string) || 'All Funds'
    : null;
  const loanTapeSecurityStatusDate: null | string = isLoanTape
    ? ((report as any).securityStatusDate as null | string) || null
    : null;
  const loanTapeBorrowerStatusDate: null | string = isLoanTape
    ? ((report as any).borrowerStatusDate as null | string) || null
    : null;

  const reportTypeForPolling: 'ewr' | 'loan_tape' | 'pid' = isPidReport
    ? 'pid'
    : isLoanTape
      ? 'loan_tape'
      : 'ewr';
  const reportDbId =
    typeof (report as any).backendReportId === 'number'
      ? ((report as any).backendReportId as number)
      : null;

  const {
    error: pollError,
    logs,
    status: pollStatus
  } = useReportPolling({
    enabled: open && status === 'in-progress' && reportDbId !== null,
    intervalMs: 3000,
    reportId: reportDbId,
    reportType: reportTypeForPolling
  });

  const inProgressView = status === 'in-progress';
  const effectiveLogs =
    logs.length > 0
      ? logs
      : (((report as any).reportLogs as typeof logs | undefined) ?? []);

  // Format timestamp for log display (YYYY-MM-DD, h:mm A)
  // Assumes backend sends UTC timestamps - converts to user's local timezone
  const formatLogTimestamp = (timestamp: string): string => {
    try {
      // If timestamp doesn't have timezone indicator, assume it's UTC and append 'Z'
      // This ensures proper conversion to user's local timezone
      const utcTimestamp =
        timestamp.endsWith('Z') ||
        timestamp.includes('+') ||
        timestamp.includes('-', 10)
          ? timestamp
          : timestamp + 'Z';

      const date = new Date(utcTimestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = date.getHours(); // Already in local timezone
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${year}-${month}-${day}, ${displayHours}:${minutes} ${ampm}`;
    } catch {
      return timestamp;
    }
  };

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          {inProgressView ? (
            <div className="flex items-center gap-2">
              <DialogTitle>Report Details</DialogTitle>
              <StatusBadge status={status} />
            </div>
          ) : (
            <DialogTitle>Report Details</DialogTitle>
          )}
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-4 p-5">
            {/* Status / Type - only show for completed reports */}
            {!inProgressView && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <StatusBadge status={status} />
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Report Type</Label>
                  <div className="mt-1">
                    <ReportTypeBadge type={getReportType(report)} />
                  </div>
                </div>
              </div>
            )}

            {/* In-progress: simple list format matching Figma */}
            {inProgressView && (
              <div className="max-h-[360px] space-y-1 overflow-y-auto">
                {(pollError || pollStatus === 'failed') && (
                  <div className="mb-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    {pollError || 'Report generation failed'}
                  </div>
                )}

                {effectiveLogs.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No logs yet. This can take a moment…
                  </p>
                ) : (
                  effectiveLogs.map((log, idx) => {
                    const logMessage = log.context
                      ? `${log.step}${log.context ? `: ${log.context}` : ''}`
                      : log.step;

                    return (
                      <div
                        className="flex items-baseline gap-2"
                        key={`${log.timestamp}-${idx}`}
                      >
                        <span className="text-foreground text-sm leading-none">
                          •
                        </span>
                        <div className="flex min-w-0 flex-1 items-baseline justify-between gap-4">
                          <span className="text-foreground flex-1 text-sm">
                            {logMessage}
                          </span>
                          <span className="text-muted-foreground shrink-0 text-xs whitespace-nowrap">
                            {formatLogTimestamp(log.timestamp)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Completed: show existing detail grid */}
            {!inProgressView && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Created By</Label>
                  <p className="mt-1 text-sm">
                    {(report as any).createUid?.name}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created At</Label>
                  <p className="mt-1 text-sm">
                    {formatDateTime((report as any).createDate)}
                  </p>
                </div>
                {investmentName && (
                  <div>
                    <Label className="text-muted-foreground">
                      Investment Name
                    </Label>
                    <p className="mt-1 text-sm">{investmentName}</p>
                  </div>
                )}
                {isLoanTape && (
                  <div>
                    <Label className="text-muted-foreground">Fund</Label>
                    <p className="mt-1 text-sm">{loanTapeFund}</p>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">
                    {isPidReport
                      ? 'Financials Period End'
                      : isLoanTape
                        ? 'Period End'
                        : 'Period End'}
                  </Label>
                  <p className="mt-1 text-sm">
                    {isLoanTape
                      ? formatDate((report as any).periodEnd)
                      : formatDate((report as any).reportDate)}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">As of Date</Label>
                  <p className="mt-1 text-sm">
                    {formatDate((report as any).asOfDate)}
                  </p>
                </div>
                {isPidReport && periodEnd && (
                  <div>
                    <Label className="text-muted-foreground">Period End</Label>
                    <p className="mt-1 text-sm">{formatDate(periodEnd)}</p>
                  </div>
                )}
                {isLoanTape && (
                  <>
                    <div>
                      <Label className="text-muted-foreground">
                        Security Status Date
                      </Label>
                      <p className="mt-1 text-sm">
                        {formatDate(loanTapeSecurityStatusDate)}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        Borrower Status Date
                      </Label>
                      <p className="mt-1 text-sm">
                        {formatDate(loanTapeBorrowerStatusDate)}
                      </p>
                    </div>
                  </>
                )}
                {!isPidReport && (report as any).leveragePeriodEnd && (
                  <div>
                    <Label className="text-muted-foreground">
                      Leverage Period End
                    </Label>
                    <p className="mt-1 text-sm">
                      {formatDate((report as any).leveragePeriodEnd)}
                    </p>
                  </div>
                )}
                {!isPidReport && (report as any).lastUpdatedSync && (
                  <div>
                    <Label className="text-muted-foreground">
                      Last Updated
                    </Label>
                    <p className="mt-1 text-sm">
                      {formatDateTime((report as any).lastUpdatedSync)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            onClick={() => {
              onOpenChange(false);
            }}
            variant="outline"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportTypeBadge({
  type
}: {
  type: 'EWR' | 'Loan Tape' | 'Loan Tape 2.0' | 'PID' | 'PID L1';
}) {
  return (
    <Badge
      className="border-transparent bg-gray-300 text-gray-800"
      variant="secondary"
    >
      {type}
    </Badge>
  );
}

function StatusBadge({
  status
}: {
  status: 'cancelled' | 'completed' | 'in-progress';
}) {
  const variants = {
    cancelled: {
      className: 'bg-red-200 text-red-800 border-transparent',
      label: 'Cancelled'
    },
    completed: {
      className: 'bg-green-200 text-green-800 border-transparent',
      label: 'Completed'
    },
    'in-progress': {
      className: 'bg-yellow-200 text-yellow-800 border-transparent',
      label: 'In Progress'
    }
  };

  const variant = variants[status];

  return (
    <Badge
      className={variant.className}
      variant="secondary"
    >
      {variant.label}
    </Badge>
  );
}

const ITEMS_PER_PAGE = 10;

export default function Reporting() {
  const { isOpen: isSidebarOpen } = useMainSidebar();

  const [newReportOpen, setNewReportOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<
    | EWRReportHistoryNode
    | LoanTapeReportHistoryNode
    | null
    | PIDReportHistoryNode
  >(null);
  const [downloadingReportId, setDownloadingReportId] = useState<null | string>(
    null
  );
  const [viewingReportId, setViewingReportId] = useState<null | string>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [pdfData, setPdfData] = useState<null | string>(null);
  const [pdfFilename, setPdfFilename] = useState<null | string>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldOpenViewer, setShouldOpenViewer] = useState(false);
  const [queuePositionDialogOpen, setQueuePositionDialogOpen] = useState(false);
  const [queuePositionData, setQueuePositionData] =
    useState<null | QueuePositionData>(null);
  const [queuePositionError, setQueuePositionError] = useState<Error | null>(
    null
  );
  const [queuePositionReportType, setQueuePositionReportType] = useState<
    'EWR' | 'Loan Tape' | 'PID' | null
  >(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Track optimistic reports (reports being generated)
  const [optimisticReports, setOptimisticReports] = useState<Map<string, any>>(
    new Map()
  );
  // Track if we're initiating a report creation (to disable button briefly)
  const [isInitiatingReport, setIsInitiatingReport] = useState(false);
  // Track report ID that should stop loading once viewer opens
  const [reportIdWaitingForViewer, setReportIdWaitingForViewer] = useState<
    null | string
  >(null);
  // Track the optimistic report ID that's currently in the viewer
  const [optimisticReportInViewer, setOptimisticReportInViewer] = useState<
    null | string
  >(null);

  // Fetch EWR reports
  const { data, error, loading, refetch } =
    useQuery<GetEwrReportHistoryResponse>(GET_EWR_REPORT_HISTORY, {
      errorPolicy: 'all',
      fetchPolicy: 'network-only'
    });

  // Fetch PID reports
  const { data: pidData, refetch: pidRefetch } =
    useQuery<GetPidReportHistoryResponse>(GET_PID_REPORT_HISTORY, {
      errorPolicy: 'all',
      fetchPolicy: 'network-only'
    });

  // Fetch Loan Tape reports
  const { data: loanTapeData, refetch: loanTapeRefetch } =
    useQuery<GetLoanTapeReportHistoryResponse>(GET_LOAN_TAPE_REPORT_HISTORY, {
      errorPolicy: 'all',
      fetchPolicy: 'network-only'
    });

  const [getReportStatusMutation] = useMutation<GetReportStatusResponse>(
    GET_REPORT_STATUS,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache'
    }
  );

  const reportStatusPollersRef = useRef<
    Map<string, ReturnType<typeof setInterval>>
  >(new Map());

  const clearReportStatusPoller = (backendReportId: string) => {
    const existing = reportStatusPollersRef.current.get(backendReportId);
    if (existing) {
      clearInterval(existing);
      reportStatusPollersRef.current.delete(backendReportId);
    }
  };

  // When viewer opens, clear the downloading/loading state for the report that was waiting
  useEffect(() => {
    if (viewerOpen && reportIdWaitingForViewer) {
      setDownloadingReportId(null);
      setReportIdWaitingForViewer(null);
      setOptimisticReportInViewer(reportIdWaitingForViewer);
    } else if (!viewerOpen && optimisticReportInViewer) {
      const reportInViewer = optimisticReports.get(optimisticReportInViewer);
      const isPidReport = reportInViewer?.reportTypeTag === 'PID';

      setOptimisticReports(prev => {
        const newMap = new Map(prev);
        newMap.delete(optimisticReportInViewer);
        return newMap;
      });
      setOptimisticReportInViewer(null);

      if (isPidReport) {
        void pidRefetch();
      } else {
        void refetch();
      }

      // Keep Loan Tape history fresh too (Excel-only report type)
      void loanTapeRefetch();
    }
  }, [
    viewerOpen,
    reportIdWaitingForViewer,
    optimisticReportInViewer,
    optimisticReports,
    refetch,
    pidRefetch,
    loanTapeRefetch
  ]);

  useEffect(() => {
    return () => {
      for (const [, interval] of reportStatusPollersRef.current.entries()) {
        clearInterval(interval);
      }
      reportStatusPollersRef.current.clear();
    };
  }, []);

  const startReportStatusPolling = (options: {
    backendReportId: string;
    onCompleted: () => Promise<void> | void;
    onFailed: (message: string) => Promise<void> | void;
    optimisticId?: string;
    reportType: 'ewr' | 'loan_tape' | 'pid';
  }) => {
    const backendReportId = String(options.backendReportId).trim();
    if (!backendReportId) return;

    if (reportStatusPollersRef.current.has(backendReportId)) return;
    const optimisticId = options.optimisticId;

    const pollOnce = async () => {
      try {
        const result = await getReportStatusMutation({
          variables: {
            reportId: backendReportId,
            reportType: options.reportType
          }
        });

        const payload = result.data?.getReportStatus;
        if (!payload) return;
        const normalizedStatus = (payload.generationStatus || '').toLowerCase();

        if (optimisticId) {
          const id = optimisticId;
          setOptimisticReports(prev => {
            if (!prev.has(id)) return prev;
            const existing = prev.get(id);
            const next = {
              ...existing,
              backendReportId,
              errorMessage: payload.errorMessage,
              generationStatus: normalizedStatus || payload.generationStatus,
              reportLogs: payload.reportLogs
            };
            const newMap = new Map(prev);
            newMap.set(id, next);
            return newMap;
          });
        }

        if (normalizedStatus === 'completed') {
          clearReportStatusPoller(backendReportId);
          await options.onCompleted();
        } else if (normalizedStatus === 'failed') {
          clearReportStatusPoller(backendReportId);
          await options.onFailed(
            payload.errorMessage ||
              payload.message ||
              'Report generation failed'
          );
        }
      } catch (error) {
        // Keep polling; transient network errors are expected sometimes.
      }
    };

    void pollOnce();
    const interval = setInterval(() => {
      void pollOnce();
    }, 2500);

    reportStatusPollersRef.current.set(backendReportId, interval);
  };

  // Create report mutation
  // Create EWR report mutation
  const [createReport] = useMutation(CREATE_EWR_REPORT, {
    awaitRefetchQueries: false,
    errorPolicy: 'all'
  });

  // Get/download PID report mutation
  const [getPidReport, { loading: downloadingPid }] =
    useMutation<GetPidReportResponse>(GET_PID_REPORT, {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      onCompleted: (data: any) => {
        if (data.getPidReport.ok && data.getPidReport.data) {
          const pdfData = data.getPidReport.data;
          let filename = data.getPidReport.filename || 'PID_Report.pdf';

          // Remove numbers from the end of the filename (before the extension)
          const filenameWithoutExtension = filename.replace(/\.[^/.]+$/, '');
          const extension = filename.match(/\.[^/.]+$/) || ['.pdf'];
          const cleanedName = filenameWithoutExtension.replace(/[_0-9]+$/, '');
          filename = cleanedName + extension[0];

          // Check if this is for viewing or downloading
          if (shouldOpenViewer || viewingReportId) {
            // Keep downloading state until viewer is actually opened
            // Set reportIdWaitingForViewer so the effect can clear it when viewerOpen becomes true
            setReportIdWaitingForViewer(downloadingReportId);
            // Show in viewer - close queue dialog if open
            setQueuePositionDialogOpen(false);
            setPdfData(pdfData);
            setPdfFilename(filename);
            setViewerOpen(true);
            setViewingReportId(null);
            setShouldOpenViewer(false);
          } else {
            // Download the PDF
            const link = document.createElement('a');
            link.href = `data:application/pdf;base64,${pdfData}`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadingReportId(null);
            toast.success('PID Report downloaded successfully');
          }
        } else {
          // PDF not ready yet - don't clear loading state if we're waiting for viewer
          // The retry logic will handle fetching again
          if (!shouldOpenViewer) {
            const errorMessage =
              data?.getPidReport?.message || 'Failed to retrieve PID report';
            toast.error(errorMessage);
            setShouldOpenViewer(false);
            setDownloadingReportId(null);
          }
          // If shouldOpenViewer is true, keep loading state and let retry logic handle it
        }
      },
      onError: (error: any) => {
        // Don't clear loading state if we're waiting for viewer - let retry logic handle it
        if (!shouldOpenViewer) {
          setShouldOpenViewer(false);
          setDownloadingReportId(null);

          const errorMessage =
            error.graphQLErrors?.[0]?.message ||
            error.networkError?.message ||
            error.message ||
            'Failed to retrieve PID report';
          toast.error(`Error retrieving PID Report: ${errorMessage}`);
        }
        // If shouldOpenViewer is true, keep loading state and let retry logic handle it
      }
    });

  // Create PID report mutation
  const [createPidReport] = useMutation(CREATE_PID_REPORT, {
    awaitRefetchQueries: false,
    errorPolicy: 'all'
  });

  // Create Loan Tape report mutation
  const [createLoanTapeReport] = useMutation(CREATE_LOAN_TAPE_REPORT, {
    awaitRefetchQueries: false,
    errorPolicy: 'all'
  });

  // Query for queue position
  const [getQueuePosition, { loading: loadingQueuePosition }] = useLazyQuery(
    GET_QUEUE_POSITION,
    {
      errorPolicy: 'all',
      fetchPolicy: 'network-only'
    }
  );

  // Get/download report mutation (supports both EWR and PID)
  const [getReport, { loading: downloading }] =
    useMutation<GetEwrReportResponse>(GET_EWR_REPORT, {
      onCompleted: (data: any) => {
        if (data.getEwrReport.ok && data.getEwrReport.data) {
          const pdfData = data.getEwrReport.data;
          let filename = data.getEwrReport.filename || 'EWR_Report.pdf';

          // Remove numbers from the end of the filename (before the extension)
          // Example: "EWR_Report_123.pdf" -> "EWR_Report.pdf"
          const filenameWithoutExtension = filename.replace(/\.[^/.]+$/, '');
          const extension = filename.match(/\.[^/.]+$/) || ['.pdf'];
          // Remove trailing numbers and underscores
          const cleanedName = filenameWithoutExtension.replace(/[_0-9]+$/, '');
          filename = cleanedName + extension[0];

          // Check if this is for viewing or downloading
          if (shouldOpenViewer || viewingReportId) {
            // Keep downloading state until viewer is actually opened
            // Set reportIdWaitingForViewer so the effect can clear it when viewerOpen becomes true
            setReportIdWaitingForViewer(downloadingReportId);
            // Show in viewer - close queue dialog if open
            setQueuePositionDialogOpen(false);
            setPdfData(pdfData);
            setPdfFilename(filename);
            setViewerOpen(true);
            setViewingReportId(null);
            setShouldOpenViewer(false);
          } else {
            // Download the PDF
            const link = document.createElement('a');
            link.href = `data:application/pdf;base64,${pdfData}`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Report downloaded successfully');
            setDownloadingReportId(null);
          }
        } else {
          // PDF not ready yet - don't clear loading state if we're waiting for viewer
          // The retry logic will handle fetching again
          if (!shouldOpenViewer) {
            const errorMessage =
              data.getEwrReport.message || 'Failed to retrieve report';
            toast.error(errorMessage);
            setDownloadingReportId(null);
            setViewingReportId(null);
            setShouldOpenViewer(false);
          }
          // If shouldOpenViewer is true, keep loading state and let retry logic handle it
        }
      },
      onError: (error: any) => {
        // Clear states on error
        setDownloadingReportId(null);
        setViewingReportId(null);
        setShouldOpenViewer(false);

        // Extract more detailed error message
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          error.networkError?.message ||
          error.message ||
          'Failed to retrieve report';
        toast.error(`Error retrieving Early Warning Report: ${errorMessage}`);
      }
    });

  const [getLoanTapeReport] = useMutation<GetLoanTapeReportResponse>(
    GET_LOAN_TAPE_REPORT,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      onCompleted: (data: any) => {
        if (data.getLoanTapeReport.ok && data.getLoanTapeReport.data) {
          const excelData = data.getLoanTapeReport.data;
          const filename =
            data.getLoanTapeReport.filename || 'Loan_Tape_Report.xlsx';
          downloadBase64File({ dataBase64: excelData, filename });
          // Loan Tape doesn't open a PDF viewer, so we should clean up optimistic rows here.
          setOptimisticReports(prev => {
            if (!downloadingReportId || !prev.has(downloadingReportId))
              return prev;
            const newMap = new Map(prev);
            newMap.delete(downloadingReportId);
            return newMap;
          });
          setDownloadingReportId(null);
          toast.success('Loan Tape downloaded successfully');
          // Refetch the loan tape history to show the completed report
          void loanTapeRefetch();
        } else {
          const errorMessage =
            data.getLoanTapeReport.message || 'Failed to retrieve Loan Tape';
          toast.error(errorMessage);
          setDownloadingReportId(null);
        }
      },
      onError: (error: any) => {
        setDownloadingReportId(null);
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          error.networkError?.message ||
          error.message ||
          'Failed to retrieve Loan Tape';
        toast.error(`Error retrieving Loan Tape: ${errorMessage}`);
      }
    }
  );

  const handleNewReport = () => {
    setNewReportOpen(true);
  };

  const handleCreateReport = async (formData: {
    asOfDate: Date;
    assetId?: string;
    assetName?: string;
    borrowerStatusDate?: Date;
    financialsPeriodEnd?: Date;
    fund?: string;
    leveragePeriodEnd?: Date;
    periodEnd?: Date;
    reportDate: Date;
    reportType: 'EWR' | 'Loan Tape' | 'PID';
    securityStatusDate?: Date;
  }) => {
    try {
      // Set initiating state to disable button briefly
      setIsInitiatingReport(true);

      // Determine reportType parameter for queue position query
      const queueReportType =
        formData.reportType === 'EWR'
          ? 'early_warning_report'
          : formData.reportType === 'PID'
            ? 'portfolio_information_dashboard'
            : formData.reportType === 'Loan Tape'
              ? 'loan_tape'
              : null;

      // Show queue position dialog immediately
      setQueuePositionDialogOpen(true);
      setQueuePositionData(null); // Reset data while loading
      setQueuePositionError(null); // Reset error
      // Store report type for time calculation
      setQueuePositionReportType(formData.reportType || null);

      // Fetch queue position IMMEDIATELY (before creating report) so it can be tested independently
      // Pass reportType to get estimated time including the user's report
      // Backend calculates: time for jobs ahead + time for user's report type
      if (queueReportType) {
        try {
          const result = await getQueuePosition({
            variables: {
              reportType: queueReportType // Backend will calculate total time including user's report
            }
          });

          // Check for errors in the result
          if (result.error) {
            const apolloError = result.error as any;
            const errorMessage =
              apolloError?.graphQLErrors?.[0]?.message ||
              apolloError?.networkError?.message ||
              apolloError?.message ||
              'Failed to fetch queue position';
            setQueuePositionError(new Error(errorMessage));
            setQueuePositionData(null);
          } else if (result.data) {
            const data = result.data as any;
            if (data.sierraReportPosition) {
              setQueuePositionData(data.sierraReportPosition);
              setQueuePositionError(null);
            } else {
              setQueuePositionData(null);
              setQueuePositionError(
                new Error('Queue position data not available')
              );
            }
          } else {
            setQueuePositionData(null);
            setQueuePositionError(
              new Error('No data returned from queue position query')
            );
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to fetch queue position';
          setQueuePositionError(new Error(errorMessage));
          setQueuePositionData(null);
        }
      }

      if (formData.reportType === 'EWR') {
        const optimisticId = `optimistic-ewr-${Date.now()}`;
        const reportDateStr = format(formData.reportDate, 'yyyy-MM-dd');
        const asOfDateStr = format(formData.asOfDate, 'yyyy-MM-dd');
        const leveragePeriodEndStr = formData.leveragePeriodEnd
          ? format(formData.leveragePeriodEnd, 'yyyy-MM-dd')
          : null;

        const optimisticReport: any = {
          asOfDate: asOfDateStr,
          createDate: new Date().toISOString(),
          createUid: {
            id: '',
            name: 'You'
          },
          displayName: '',
          id: optimisticId,
          leveragePeriodEnd: leveragePeriodEndStr,
          reportDate: reportDateStr,
          reportTypeTag: 'EWR' as const
        };

        setOptimisticReports(prev =>
          new Map(prev).set(optimisticId, optimisticReport)
        );

        setDownloadingReportId(optimisticId);
        setCurrentPage(1);
        setIsInitiatingReport(false);

        void (async () => {
          try {
            const result = await createReport({
              variables: {
                asOfDate: asOfDateStr,
                leveragePeriodEnd: leveragePeriodEndStr,
                reportDate: reportDateStr
              }
            });

            const payload = (result.data as any)?.createEwrReport;
            if (!payload?.ok) {
              throw new Error(payload?.message || 'Failed to create report');
            }

            const backendReportDbId = Number(payload.reportId);
            if (!Number.isFinite(backendReportDbId)) {
              throw new Error('Report ID was not returned by the server');
            }

            toast.message('Report queued for generation');

            // Attach backend ID immediately so dialogs can poll and actions can work later.
            setOptimisticReports(prev => {
              if (!prev.has(optimisticId)) return prev;
              const existing = prev.get(optimisticId);
              const newMap = new Map(prev);
              newMap.set(optimisticId, {
                ...existing,
                backendReportId: backendReportDbId
              });
              return newMap;
            });

            startReportStatusPolling({
              backendReportId: encodeReportId(backendReportDbId),
              onCompleted: async () => {
                setShouldOpenViewer(true);

                const fetchPdfWithRetry = async (retries = 10) => {
                  try {
                    const result: any = await getReport({
                      variables: { reportId: encodeReportId(backendReportDbId) }
                    });
                    if (!result.data?.getEwrReport?.ok && retries > 0) {
                      setTimeout(() => {
                        void fetchPdfWithRetry(retries - 1);
                      }, 2000);
                    } else if (
                      !result.data?.getEwrReport?.ok &&
                      retries === 0
                    ) {
                      setDownloadingReportId(null);
                      setShouldOpenViewer(false);
                      toast.error(
                        'PDF is taking longer than expected. Please try again in a moment.'
                      );
                    }
                  } catch (error) {
                    // Retry on error if we have retries left
                    if (retries > 0) {
                      setTimeout(() => {
                        void fetchPdfWithRetry(retries - 1);
                      }, 2000);
                    } else {
                      // Final failure after all retries - clear loading state
                      setDownloadingReportId(null);
                      setShouldOpenViewer(false);
                      toast.error(
                        'PDF is taking longer than expected. Please try again in a moment.'
                      );
                    }
                  }
                };

                void fetchPdfWithRetry();

                // No need to refetch - optimistic report state is already updated via polling
                // The report will show as completed in the UI without a page refresh
              },
              onFailed: async (message: string) => {
                toast.error(message);
                setDownloadingReportId(null);
                setQueuePositionDialogOpen(false);
                setOptimisticReports(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(optimisticId);
                  return newMap;
                });
              },
              optimisticId,
              reportType: 'ewr'
            });
          } catch (error) {
            const message =
              error instanceof Error
                ? error.message
                : 'Failed to create report';
            toast.error(message);
            setDownloadingReportId(null);
            setQueuePositionDialogOpen(false);
            setOptimisticReports(prev => {
              const newMap = new Map(prev);
              newMap.delete(optimisticId);
              return newMap;
            });
          }
        })();

        // After mutation is called (not completed), refetch will happen automatically
        // The optimistic report will be replaced when real data comes back
      } else if (formData.reportType === 'PID') {
        // Create PID Report
        if (
          !formData.assetId ||
          !formData.periodEnd ||
          !formData.financialsPeriodEnd
        ) {
          toast.error(
            'Asset ID, Period End, and Financials Period End are required for PID reports'
          );
          setIsInitiatingReport(false);
          setQueuePositionDialogOpen(false);
          return;
        }

        // Create optimistic report entry
        const optimisticId = `optimistic-pid-${Date.now()}`;
        const asOfDateStr = format(formData.asOfDate, 'yyyy-MM-dd');
        const periodEndStr = format(formData.periodEnd, 'yyyy-MM-dd');
        const financialsPeriodEndStr = format(
          formData.financialsPeriodEnd,
          'yyyy-MM-dd'
        );

        const optimisticReport: any = {
          asOfDate: asOfDateStr,
          createDate: new Date().toISOString(),
          createUid: {
            id: '',
            name: 'You'
          },
          generatedBy: {
            id: '',
            name: 'You'
          },
          generatedDate: new Date().toISOString(),
          id: optimisticId,
          reportDate: financialsPeriodEndStr,
          reportId: {
            asOfDate: asOfDateStr,
            financialsPeriodEnd: financialsPeriodEndStr,
            id: '',
            investmentName: formData.assetName || '',
            periodEnd: periodEndStr
          },
          reportTypeTag: 'PID' as const
        };

        setOptimisticReports(prev =>
          new Map(prev).set(optimisticId, optimisticReport)
        );

        setDownloadingReportId(optimisticId);
        setCurrentPage(1);
        setIsInitiatingReport(false);

        void (async () => {
          try {
            const result = await createPidReport({
              variables: {
                asOfDate: asOfDateStr,
                assetId: formData.assetId,
                financialsPeriodEnd: financialsPeriodEndStr,
                periodEnd: periodEndStr
              }
            });

            const payload = (result.data as any)?.createPidReport;
            if (!payload?.ok) {
              throw new Error(
                payload?.message || 'Failed to create PID report'
              );
            }

            const backendReportDbId = Number(payload.reportId);
            if (!Number.isFinite(backendReportDbId)) {
              throw new Error('Report ID was not returned by the server');
            }

            toast.message('PID report queued for generation');

            // Attach backend ID immediately so dialogs can poll and actions can work later.
            setOptimisticReports(prev => {
              if (!prev.has(optimisticId)) return prev;
              const existing = prev.get(optimisticId);
              const newMap = new Map(prev);
              const next = {
                ...existing,
                backendReportId: backendReportDbId,
                reportId: {
                  ...(existing?.reportId || {}),
                  id: encodeReportId(backendReportDbId)
                }
              };
              newMap.set(optimisticId, next);
              return newMap;
            });

            startReportStatusPolling({
              backendReportId: encodeReportId(backendReportDbId),
              onCompleted: async () => {
                // Keep loading state active until PDF viewer opens
                // downloadingReportId should already be set, but ensure it stays set
                setShouldOpenViewer(true);

                // Retry fetching PDF until it's ready
                // Keep downloadingReportId set until PDF viewer opens
                const fetchPdfWithRetry = async (retries = 10) => {
                  try {
                    const result: any = await getPidReport({
                      variables: { reportId: encodeReportId(backendReportDbId) }
                    });
                    if (!result.data?.getPidReport?.ok && retries > 0) {
                      setTimeout(() => {
                        void fetchPdfWithRetry(retries - 1);
                      }, 2000);
                    } else if (
                      !result.data?.getPidReport?.ok &&
                      retries === 0
                    ) {
                      setDownloadingReportId(null);
                      setShouldOpenViewer(false);
                      toast.error(
                        'PDF is taking longer than expected. Please try again in a moment.'
                      );
                    }
                  } catch (error) {
                    if (retries > 0) {
                      setTimeout(() => {
                        void fetchPdfWithRetry(retries - 1);
                      }, 2000);
                    } else {
                      setDownloadingReportId(null);
                      setShouldOpenViewer(false);
                      toast.error(
                        'PDF is taking longer than expected. Please try again in a moment.'
                      );
                    }
                  }
                };

                void fetchPdfWithRetry();

                // No need to refetch - optimistic report state is already updated via polling
                // The report will show as completed in the UI without a page refresh
              },
              onFailed: async (message: string) => {
                toast.error(message);
                setDownloadingReportId(null);
                setQueuePositionDialogOpen(false);
                setOptimisticReports(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(optimisticId);
                  return newMap;
                });
              },
              optimisticId,
              reportType: 'pid'
            });
          } catch (error) {
            const message =
              error instanceof Error
                ? error.message
                : 'Failed to create PID report';
            toast.error(message);
            setDownloadingReportId(null);
            setQueuePositionDialogOpen(false);
            setOptimisticReports(prev => {
              const newMap = new Map(prev);
              newMap.delete(optimisticId);
              return newMap;
            });
          }
        })();
      } else {
        // Create Loan Tape report (Excel only)
        if (
          !formData.fundId ||
          !formData.periodEnd ||
          !formData.asOfDate ||
          !formData.securityStatusDate ||
          !formData.borrowerStatusDate
        ) {
          toast.error(
            'Fund, Period End, As of Date, Security Status Date, and Borrower Status Date are required for Loan Tape reports'
          );
          setIsInitiatingReport(false);
          setQueuePositionDialogOpen(false);
          return;
        }

        const optimisticId = `optimistic-loan-tape-${Date.now()}`;
        const periodEndStr = format(formData.periodEnd, 'yyyy-MM-dd');
        const asOfDateStr = format(formData.asOfDate, 'yyyy-MM-dd');
        const securityStatusDateStr = format(
          formData.securityStatusDate,
          'yyyy-MM-dd'
        );
        const borrowerStatusDateStr = format(
          formData.borrowerStatusDate,
          'yyyy-MM-dd'
        );

        const optimisticReport: any = {
          asOfDate: asOfDateStr,
          createDate: new Date().toISOString(),
          createUid: {
            id: '',
            name: 'You'
          },
          errorMessage: null,
          fundId: formData.fundId,
          fundName: formData.fundId === '0' ? 'All Funds' : 'Loading...',
          generationStatus: 'pending',
          id: optimisticId,
          name: '',
          periodEnd: periodEndStr,
          reportDate: periodEndStr, // compatibility with existing display
          reportTypeTag: 'LOAN_TAPE' as const
        };

        setOptimisticReports(prev =>
          new Map(prev).set(optimisticId, optimisticReport)
        );

        setDownloadingReportId(optimisticId);
        setCurrentPage(1);
        setIsInitiatingReport(false);

        void (async () => {
          try {
            const result = await createLoanTapeReport({
              variables: {
                asOfDate: asOfDateStr,
                borrowerStatusDate: borrowerStatusDateStr,
                fundId: formData.fundId,
                periodEnd: periodEndStr,
                securityStatusDate: securityStatusDateStr
              }
            });

            const payload = (result.data as any)?.createLoanTapeReport;
            if (!payload?.ok) {
              throw new Error(payload?.message || 'Failed to create Loan Tape');
            }

            const backendReportDbId = Number(payload.reportId);
            if (!Number.isFinite(backendReportDbId)) {
              throw new Error('Report ID was not returned by the server');
            }

            toast.message('Loan Tape queued for generation');

            setOptimisticReports(prev => {
              if (!prev.has(optimisticId)) return prev;
              const existing = prev.get(optimisticId);
              const newMap = new Map(prev);
              newMap.set(optimisticId, {
                ...existing,
                backendReportId: backendReportDbId
              });
              return newMap;
            });

            startReportStatusPolling({
              backendReportId: encodeReportId(backendReportDbId),
              onCompleted: async () => {
                const fetchExcelWithRetry = async (retries = 10) => {
                  try {
                    const result = await getLoanTapeReport({
                      variables: { reportId: encodeReportId(backendReportDbId) }
                    });
                    if (!result.data?.getLoanTapeReport?.ok && retries > 0) {
                      setTimeout(() => {
                        void fetchExcelWithRetry(retries - 1);
                      }, 2000);
                    } else if (
                      !result.data?.getLoanTapeReport?.ok &&
                      retries === 0
                    ) {
                      setDownloadingReportId(null);
                      toast.error(
                        'Excel is taking longer than expected. Please try again in a moment.'
                      );
                    }
                  } catch (error) {
                    if (retries > 0) {
                      setTimeout(() => {
                        void fetchExcelWithRetry(retries - 1);
                      }, 2000);
                    } else {
                      setDownloadingReportId(null);
                      toast.error(
                        'Excel is taking longer than expected. Please try again in a moment.'
                      );
                    }
                  }
                };

                void fetchExcelWithRetry();
              },
              onFailed: async (message: string) => {
                toast.error(message);
                setDownloadingReportId(null);
                setQueuePositionDialogOpen(false);
                setOptimisticReports(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(optimisticId);
                  return newMap;
                });
                // Refetch the loan tape history to show the failed report
                void loanTapeRefetch();
              },
              optimisticId,
              reportType: 'loan_tape'
            });
          } catch (error) {
            const message =
              error instanceof Error
                ? error.message
                : 'Failed to create Loan Tape';
            toast.error(message);
            setDownloadingReportId(null);
            setQueuePositionDialogOpen(false);
            setOptimisticReports(prev => {
              const newMap = new Map(prev);
              newMap.delete(optimisticId);
              return newMap;
            });
          }
        })();
      }
    } catch (error) {
      // Error is handled by the mutation's onError callback
      setIsInitiatingReport(false);
      setQueuePositionDialogOpen(false);
    }
  };

  const handleViewReport = (report: any) => {
    // Check if this is a PID report
    const isPidReport =
      'reportTypeTag' in report && report.reportTypeTag === 'PID';
    const isLoanTapeReport =
      'reportTypeTag' in report && report.reportTypeTag === 'LOAN_TAPE';

    // For PID reports, use reportId.id; for EWR reports, use id
    const baseReportId =
      isPidReport && 'reportId' in report && report.reportId
        ? report.reportId.id
        : report.id;
    const reportId = report.backendReportId || baseReportId;

    if (!reportId || String(reportId).trim() === '') {
      toast.error('Report ID not available');
      return;
    }

    const trimmedReportId = String(reportId).trim();
    if (!trimmedReportId) {
      toast.error('Invalid report ID');
      return;
    }

    // Set viewing state and fetch the report using the appropriate mutation
    // Use the history node ID for tracking which row is loading
    setViewingReportId(trimmedReportId);
    setDownloadingReportId(report.id); // Use report.id for row identification

    if (isLoanTapeReport) {
      // Loan Tape has no PDF viewer - eye icon should download Excel
      setViewingReportId(null);
      void getLoanTapeReport({
        variables: {
          reportId: trimmedReportId
        }
      });
      return;
    }

    if (isPidReport) {
      void getPidReport({
        variables: {
          reportId: trimmedReportId
        }
      });
    } else {
      void getReport({
        variables: {
          reportId: trimmedReportId
        }
      });
    }
  };

  const handleDownloadReport = (report: any) => {
    // Check if this is a PID report
    const isPidReport =
      'reportTypeTag' in report && report.reportTypeTag === 'PID';
    const isLoanTapeReport =
      'reportTypeTag' in report && report.reportTypeTag === 'LOAN_TAPE';

    // For PID reports, use reportId.id; for EWR reports, use id
    const baseReportId =
      isPidReport && 'reportId' in report && report.reportId
        ? report.reportId.id
        : report.id;
    const reportId = report.backendReportId || baseReportId;

    // Validate reportId
    if (!reportId || String(reportId).trim() === '') {
      toast.error(
        'Report ID not available. This report may not be ready for download.'
      );
      return;
    }

    const trimmedReportId = String(reportId).trim();
    if (!trimmedReportId) {
      toast.error('Invalid report ID');
      return;
    }

    // Set downloading state - use report.id for row identification
    setDownloadingReportId(report.id);

    // Use the appropriate mutation based on report type
    if (isLoanTapeReport) {
      void getLoanTapeReport({
        variables: {
          reportId: trimmedReportId
        }
      });
    } else if (isPidReport) {
      void getPidReport({
        variables: {
          reportId: trimmedReportId
        }
      });
    } else {
      void getReport({
        variables: {
          reportId: trimmedReportId
        }
      });
    }
  };

  const handleInfo = (
    report:
      | EWRReportHistoryNode
      | LoanTapeReportHistoryNode
      | PIDReportHistoryNode
  ) => {
    setSelectedReport(report);
    setInfoDialogOpen(true);
  };

  // Remove optimistic reports when the mutation completes successfully
  // We don't match by parameters - we just remove optimistic reports when we know the real one is ready

  // Combine EWR and PID reports with a type tag
  // Filter out reports with invalid IDs and ensure data integrity
  // Sort by createDate descending (newest first)
  const ewrReportsWithType = (((data as any)?.ewrReports as any[]) || [])
    .filter((report: any) => report?.id)
    .map((report: any) => ({ ...report, reportTypeTag: 'EWR' as const }));

  const pidReportsWithType = (
    ((pidData as any)?.pidReportHistory as any[]) || []
  )
    .filter((report: any) => report?.id)
    .map((report: any) => ({
      ...report,
      asOfDate: report.reportId?.asOfDate || '',
      createDate: report.generatedDate,
      createUid: report.generatedBy,
      // Map PID fields to match EWR structure for display
      // Use financialsPeriodEnd if available, otherwise fallback to empty string
      reportDate: report.reportId?.financialsPeriodEnd || '',
      reportTypeTag: 'PID' as const
      // Note: PID uses periodEnd (kept in reportId), not leveragePeriodEnd
    }));

  // Deduplicate PID reports by actual report ID (backend bug workaround)
  // Keep only the most recent history entry for each unique report ID
  const uniquePidReports = pidReportsWithType.reduce<any[]>(
    (acc: any[], report: any) => {
      const reportId = report.reportId?.id;
      if (!reportId) return acc;

      const existing = acc.find((r: any) => r.reportId?.id === reportId);
      if (!existing) {
        acc.push(report);
      } else {
        // Keep the one with the most recent generatedDate
        const existingDate = new Date(existing.generatedDate).getTime();
        const currentDate = new Date(report.generatedDate).getTime();
        if (currentDate > existingDate) {
          // Replace with more recent entry
          const index = acc.indexOf(existing);
          acc[index] = report;
        }
      }
      return acc;
    },
    []
  );

  const loanTapeReportsWithType = (
    ((loanTapeData as any)?.loanTapeReports as any[]) || []
  )
    .filter((report: any) => report?.id)
    .map((report: any) => ({
      ...report,
      // Backfill fields expected by the shared history table
      createDate: report.generatedDate || null,
      createUid: { id: '', name: 'System' },
      leveragePeriodEnd: null,
      reportDate: report.periodEnd,
      reportTypeTag: 'LOAN_TAPE' as const
    }));

  // Add optimistic reports to the list, but only if they don't exist in the backend
  const optimisticReportsList = Array.from(optimisticReports.values()).filter(
    (optimisticReport: any) => {
      // Check if this optimistic report already exists in the backend
      const isPid = optimisticReport.reportTypeTag === 'PID';

      if (isPid) {
        // For PID reports, check if it exists in the real data
        return !uniquePidReports.some(
          (r: any) =>
            r.reportId?.id === optimisticReport.reportId?.id &&
            optimisticReport.reportId?.id
        );
      } else {
        // For EWR reports, check if it exists in the real data
        return !ewrReportsWithType.some(
          (r: any) => r.id === optimisticReport.id
        );
      }
    }
  );

  const allReportsSorted = [
    ...ewrReportsWithType,
    ...uniquePidReports,
    ...loanTapeReportsWithType,
    ...optimisticReportsList
  ].sort((a, b) => {
    // Sort by createDate descending (newest first)
    const dateA = a.createDate ? new Date(a.createDate).getTime() : 0;
    const dateB = b.createDate ? new Date(b.createDate).getTime() : 0;
    return dateB - dateA; // Descending order
  });

  // Filter reports based on search term
  const allReports = allReportsSorted.filter(report => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();

    // Search in creator name
    const creatorName = report.createUid?.name || '';
    if (creatorName.toLowerCase().includes(searchLower)) return true;

    // Search in report type
    const reportType = report.reportTypeTag || '';
    if (reportType.toLowerCase().includes(searchLower)) return true;

    // Search in fund name (Loan Tape reports)
    const fundName = report.fundName || '';
    if (fundName.toLowerCase().includes(searchLower)) return true;

    // Search in investment name (PID reports)
    const investmentName =
      'reportId' in report && report.reportId
        ? report.reportId.investmentName || ''
        : '';
    if (investmentName.toLowerCase().includes(searchLower)) return true;

    return false;
  });

  // Calculate pagination
  const totalReports = allReports.length;
  const totalPages = Math.ceil(totalReports / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const reports = allReports.slice(startIndex, endIndex);

  // Sidebar reduces available width. To prevent horizontal scroll only in the
  // sidebar-open layout, hide lower-priority columns earlier.
  const hideFundInvestment = isSidebarOpen
    ? 'max-[1249px]:hidden'
    : 'max-[999px]:hidden';
  const hidePeriodAsOf = isSidebarOpen
    ? 'max-[1049px]:hidden'
    : 'max-[649px]:hidden';
  const hideCreatedDate = isSidebarOpen
    ? 'max-[859px]:hidden'
    : 'max-[799px]:hidden';

  // Reset to page 1 if current page is out of bounds
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  // Helper function to generate page numbers for pagination
  const getPageNumbers = (): ('ellipsis' | number)[] => {
    const pages: ('ellipsis' | number)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  const liveSelectedReport = selectedReport
    ? (optimisticReports.get(selectedReport.id) ?? selectedReport)
    : null;

  return (
    <div className="flex h-full flex-col">
      {/* Filter/Action Bar */}
      <div className="bg-sidebar flex justify-between gap-x-2.5 border-b px-5 py-1.5 md:px-14">
        <div className="flex items-end gap-x-2.5">
          <div className="w-[300px]">
            <Field className="gap-0">
              <FieldLabel
                className="h-5"
                htmlFor="search-field"
              >
                Search
              </FieldLabel>
              <Input
                id="search-field"
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                placeholder="Search reports..."
                value={searchTerm}
              />
            </Field>
          </div>
        </div>
        <div className="flex items-end gap-x-2.5">
          <Button
            disabled={isInitiatingReport}
            onClick={handleNewReport}
          >
            <PlusIcon />
            New Report
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col p-5 md:px-14 md:py-6">
          {loading ? (
            <div className="grid flex-1 place-content-center">
              <Loader2Icon className="text-muted-foreground h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-6">
              <h3 className="font-semibold text-red-800">
                Error loading reports
              </h3>
              <p className="mt-2 text-sm text-red-700">{error.message}</p>
              {(error as any).graphQLErrors &&
                (error as any).graphQLErrors.length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    {(error as any).graphQLErrors.map(
                      (err: any, idx: number) => (
                        <p key={idx}>{err?.message}</p>
                      )
                    )}
                  </div>
                )}
              {(error as any).networkError && (
                <p className="mt-2 text-xs text-red-600">
                  Network error: {(error as any).networkError?.message}
                </p>
              )}
              <Button
                className="mt-4"
                onClick={() => void refetch()}
                size="sm"
                variant="outline"
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px] text-xs font-semibold max-[449px]:hidden">
                        Created By
                      </TableHead>
                      <TableHead
                        className={`w-[130px] text-xs font-semibold ${hideCreatedDate}`}
                      >
                        Created Date
                      </TableHead>
                      <TableHead
                        className={`w-[80px] text-xs font-semibold ${hideFundInvestment}`}
                      >
                        Fund
                      </TableHead>
                      <TableHead
                        className={`w-[120px] text-xs font-semibold ${hideFundInvestment}`}
                      >
                        Investment
                      </TableHead>
                      <TableHead
                        className={`w-[95px] text-xs font-semibold ${hidePeriodAsOf}`}
                      >
                        Period End
                      </TableHead>
                      <TableHead
                        className={`w-[95px] text-xs font-semibold ${hidePeriodAsOf}`}
                      >
                        As of Date
                      </TableHead>
                      <TableHead className="w-[86px] text-xs font-semibold max-[349px]:hidden">
                        Status
                      </TableHead>
                      <TableHead className="w-[96px] text-xs font-semibold">
                        Report Type
                      </TableHead>
                      <TableHead className="w-[104px] text-right text-xs font-semibold">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {totalReports === 0 ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          className="p-0"
                          colSpan={9}
                        >
                          <Empty className="px-0! pt-5! pb-0!">
                            <EmptyContent>
                              <Button onClick={handleNewReport}>
                                <PlusIcon />
                                New Report
                              </Button>
                            </EmptyContent>
                          </Empty>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reports.map(report => {
                        const row = report;
                        const status = getReportStatus(
                          report,
                          optimisticReports
                        );
                        const reportType = getReportType(report);
                        const createdByName = row.createUid?.name || 'Unknown';
                        const createdByInitials = getInitials(createdByName);

                        // Check report type from the tag we added
                        const reportTag = report.reportTypeTag;
                        const isPidReport = reportTag === 'PID';
                        const isLoanTapeReport = reportTag === 'LOAN_TAPE';

                        // Get display values based on report type
                        // Investment Name - PID only
                        const investmentName =
                          isPidReport && 'reportId' in report && report.reportId
                            ? report.reportId.investmentName || ''
                            : '';

                        // Period End - EWR uses reportDate, PID uses periodEnd
                        const periodEndValue = isLoanTapeReport
                          ? (report.periodEnd as string) || ''
                          : isPidReport &&
                              'reportId' in report &&
                              report.reportId
                            ? report.reportId.periodEnd || ''
                            : report.reportDate || '';

                        const fundValue = isLoanTapeReport
                          ? (report.fundName as string)
                          : '';
                        return (
                          <TableRow
                            className="h-[44px]"
                            key={report.id}
                          >
                            <TableCell className="h-[44px] w-[120px] px-2 align-middle whitespace-nowrap max-[449px]:hidden">
                              <div className="flex items-center gap-2">
                                <Avatar initials={createdByInitials} />
                                <div className="flex min-w-0 flex-col">
                                  <span className="truncate text-sm font-medium">
                                    {createdByName}
                                  </span>
                                  <span className="text-muted-foreground text-xs md:hidden">
                                    {formatDate(row.createDate)}
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell
                              className={`h-[44px] w-[130px] px-2 align-middle whitespace-nowrap ${hideCreatedDate}`}
                            >
                              <div
                                className="truncate"
                                title={
                                  formatDateTime(row.createDate) ?? undefined
                                }
                              >
                                {formatDateTime(row.createDate)}
                              </div>
                            </TableCell>
                            <TableCell
                              className={`h-[44px] w-[80px] px-2 align-middle whitespace-nowrap ${hideFundInvestment}`}
                            >
                              <div
                                className="truncate"
                                title={
                                  fundValue ||
                                  (isLoanTapeReport ? 'All Funds' : undefined)
                                }
                              >
                                {fundValue ||
                                  (isLoanTapeReport ? 'All Funds' : null)}
                              </div>
                            </TableCell>
                            <TableCell
                              className={`h-[44px] w-[120px] px-2 align-middle ${hideFundInvestment}`}
                            >
                              <div
                                className="truncate"
                                title={investmentName}
                              >
                                {investmentName}
                              </div>
                            </TableCell>
                            <TableCell
                              className={`h-[44px] w-[95px] px-2 align-middle whitespace-nowrap ${hidePeriodAsOf}`}
                            >
                              {periodEndValue
                                ? formatDate(periodEndValue)
                                : null}
                            </TableCell>
                            <TableCell
                              className={`h-[44px] w-[95px] px-2 align-middle whitespace-nowrap ${hidePeriodAsOf}`}
                            >
                              {formatDate(row.asOfDate)}
                            </TableCell>
                            <TableCell className="h-[44px] w-[86px] px-2 align-middle whitespace-nowrap max-[349px]:hidden">
                              <StatusBadge status={status} />
                            </TableCell>
                            <TableCell className="h-[44px] w-[96px] px-2 align-middle whitespace-nowrap">
                              <ReportTypeBadge type={reportType} />
                            </TableCell>
                            <TableCell className="h-[44px] w-[104px] px-2 align-middle whitespace-nowrap">
                              <ActionButtons
                                isDownloading={
                                  downloadingReportId === report.id
                                }
                                onDownload={() => {
                                  handleDownloadReport(report);
                                }}
                                onInfo={() => {
                                  handleInfo(report);
                                }}
                                onView={() => {
                                  handleViewReport(report);
                                }}
                                optimisticReports={optimisticReports}
                                report={report}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalReports > ITEMS_PER_PAGE && (
                <div className="flex flex-shrink-0 items-center justify-between pt-4">
                  <p className="text-muted-foreground text-sm font-normal">
                    Showing {startIndex + 1}-{Math.min(endIndex, totalReports)}{' '}
                    of {totalReports}
                  </p>
                  <Pagination className="justify-end">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          className={
                            currentPage === 1
                              ? 'pointer-events-none opacity-50'
                              : 'cursor-pointer'
                          }
                          onClick={e => {
                            e.preventDefault();
                            if (currentPage > 1) handlePreviousPage();
                          }}
                        />
                      </PaginationItem>

                      {getPageNumbers().map((page, idx) =>
                        page === 'ellipsis' ? (
                          <PaginationItem key={`ellipsis-${idx}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={page}>
                            <PaginationLink
                              className="cursor-pointer"
                              isActive={currentPage === page}
                              onClick={e => {
                                e.preventDefault();
                                handlePageClick(page);
                              }}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      )}

                      <PaginationItem>
                        <PaginationNext
                          className={
                            currentPage === totalPages
                              ? 'pointer-events-none opacity-50'
                              : 'cursor-pointer'
                          }
                          onClick={e => {
                            e.preventDefault();
                            if (currentPage < totalPages) handleNextPage();
                          }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* New Report Dialog */}
      <NewReportDialog
        existingReports={allReports}
        onCreate={handleCreateReport}
        onOpenChange={setNewReportOpen}
        open={newReportOpen}
      />

      {/* Report Info Dialog */}
      <ReportInfoDialog
        onOpenChange={setInfoDialogOpen}
        open={infoDialogOpen}
        optimisticReports={optimisticReports}
        report={liveSelectedReport}
      />

      {/* PDF Viewer Dialog */}
      <PdfViewerDialog
        description="View the Early Warning Report"
        filename={pdfFilename}
        onOpenChange={(open: boolean) => {
          setViewerOpen(open);
          if (!open) {
            // Clear PDF data when dialog closes
            setPdfData(null);
            setPdfFilename(null);
            // Clear downloading state when viewer closes
            setDownloadingReportId(null);
            setShouldOpenViewer(false);
          }
        }}
        open={viewerOpen}
        pdfData={pdfData}
        title="Early Warning Report"
      />

      {/* Queue Position Dialog */}
      <QueuePositionDialog
        error={queuePositionError}
        loading={loadingQueuePosition}
        onOpenChange={(open: boolean) => {
          setQueuePositionDialogOpen(open);
          if (!open) {
            // Clear queue data when dialog closes
            setQueuePositionData(null);
            setQueuePositionError(null);
            setQueuePositionReportType(null);
          }
        }}
        open={queuePositionDialogOpen}
        queueData={queuePositionData}
        reportType={queuePositionReportType}
      />
    </div>
  );
}
